/**
 * CassetteEditorDialog Component - Edit cassette configuration (splices and splitters)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { usePoleStore, useCableStore } from '../../stores';
import { useONUStore } from '../../stores/onuStore';
import { Pole, Cable, CassetteConfig } from '../../types';
import { FiberSignalIndicator } from '../Common/FiberSignalIndicator';
import { getFiberBadgeStyle } from '../../utils/colorUtils';
import { getFiberDisplayName, calculateBufferConfig, getFiberColorName, getFiberColorInBuffer, fiberCountFromCableType } from '../../utils/fiberColors';
import { traceOpticalPath } from '../../utils/opticalPathTracer';
import { zoradPodlaOlt } from '../../utils/cableDirection';
import { farbaVlakna } from '../../utils/customerSearch';
import { indexZakaznickychVlakien, zakaznikNaVlakne,
         indexPortovSplitterov, portNaVlakne } from '../../utils/zakaznickeVlakna';
import { getErrorMessage } from '../../utils/errorMessages';
import { zosuladZvarSOdbockou } from '../../utils/odbocky';
import { getSplitterOutputs } from '../../utils/splitterCatalog';
import { useSplitterCatalogStore } from '../../stores/splitterCatalogStore';
import { cablesAPI } from '../../services/api';
import { koniecOdOlt } from '../../utils/cableDirection';
import UndoToast from '../Toast/UndoToast';
import './Dialog.css';
import './CassetteEditorDialog.css';

interface CassetteEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  pole: Pole;
}

// Helper: Get splitter ratio (počet výstupov) z katalógu (napr. "1:8" -> 8)
const getSplitterRatio = (type: string): number => {
  return getSplitterOutputs(type);
};

// Helper: Calculate total cascade ratio for a path from OLT port
const calculateCascadeRatio = (
  oltPort: number,
  cassette: CassetteConfig,
  cables: any[],
  poles: any[]
): number => {
  let totalRatio = 1;

  // Find splitters connected to this OLT port
  const splitters = cassette.splitters || [];
  const portSplitters = splitters.filter(s =>
    s.input_source_type === 'olt_port' && s.input_olt_port === oltPort
  );

  portSplitters.forEach(splitter => {
    totalRatio *= getSplitterRatio(splitter.type);
  });

  // TODO: Recursively check downstream poles for cascade splitters
  // For now, just return the ratio from this pole

  return totalRatio;
};

export const CassetteEditorDialog: React.FC<CassetteEditorDialogProps> = ({
  isOpen,
  onClose,
  pole,
}) => {
  const { poles, updatePole } = usePoleStore();
  const { cables, fetchCables } = useCableStore();
  const { onus } = useONUStore();

  /**
   * Signál na vlákne — aby technik pri výbere hneď videl, či je tam svetlo
   * a aké silné, a nemusel to skúšať metódou pokus-omyl.
   *
   * Trasovanie sa volá raz na každé vlákno v rozbaľovačke (pri 144-vláknovom
   * kábli 144-krát), preto sa výsledky držia v pamäti. Mapa sa zahodí vždy,
   * keď sa zmenia stĺpy alebo káble — inak by po prepojení kazety ukazovala
   * starý stav.
   */
  const signalCache = useMemo(() => new Map<string, string>(), [poles, cables]);

  /**
   * Ktoré vlákno patrí ktorému zákazníkovi. Zákazník môže visieť na splittri
   * na jednom stĺpiku a fyzicky byť pripojený na inom — jeho vlákno cestou
   * prejde zvarmi. Bez tohto je na cieľovom stĺpiku vidno len zvar bez mena.
   */
  const vlaknaZakaznikov = useMemo(() => indexZakaznickychVlakien(poles), [poles]);

  /** Ktorý port zákazníckeho splittra kŕmi dané vlákno — aj keď je voľný. */
  const portyNaVlaknach = useMemo(() => indexPortovSplitterov(poles), [poles]);

  /** Prípojka — koniec vlákna, ktoré ide k zákazníkovi domov. */
  const handleAddDrop = () => {
    if (connectedCables.length === 0) {
      setError('Na tomto stĺpiku nekončí žiadny kábel, nie je čo pripojiť');
      return;
    }
    const kabel = connectedCables[0];
    setCassette({
      ...cassette,
      has_cassette: true,
      drops: [...(cassette.drops || []), { cable_id: kabel.id, fiber: 1 }],
    });
    setError(null);
  };

  const handleUpdateDrop = (index: number, pole: 'cable_id' | 'fiber' | 'label', hodnota: any) => {
    const updated = [...(cassette.drops || [])];
    updated[index] = { ...updated[index], [pole]: hodnota };
    setCassette({ ...cassette, drops: updated });
  };

  const handleRemoveDrop = (index: number) => {
    setCassette({
      ...cassette,
      drops: (cassette.drops || []).filter((_, i) => i !== index),
    });
  };

  /** Štítok „👤 meno (zo stĺpika X)" k vláknu, ak naň niekto visí. */
  const zakaznikStitok = (cableId?: number | null, fiber?: number | null) => {
    const z = zakaznikNaVlakne(vlaknaZakaznikov, cableId, fiber);
    if (!z) return null;
    return (
      <span style={{
        marginLeft: '8px', fontSize: '12px', fontWeight: 700, color: '#b45309',
      }}>
        👤 {z.meno}
        <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '4px' }}>
          (zo {z.zoStlpika})
        </span>
      </span>
    );
  };

  const splitterCatalog = useSplitterCatalogStore((s) => s.types).filter((t) => t.is_active !== false);

  const [cassette, setCassette] = useState<CassetteConfig>(
    pole.cassette || { has_cassette: false, pass_through_cables: [], splices: [], splitters: [] }
  );
  const [activeTab, setActiveTab] = useState<'pass_through' | 'splices' | 'splitters'>('pass_through');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [delim, setDelim] = useState(false);
  // Ktoré skupiny bufrov v priechode sú rozbalené; kľúč „priechod-buffer“.
  // Bez záznamu platí: rozbalený je buffer, v ktorom je niečo odbočené.
  const [otvoreneBufre, setOtvoreneBufre] = useState<Record<string, boolean>>({});
  const [oznam, setOznam] = useState<string | null>(null);

  // Undo toast state
  const [undoToast, setUndoToast] = useState<{
    visible: boolean;
    message: string;
    onUndo: () => void;
  } | null>(null);

  // Káble na tomto stĺpe, ZORADENÉ tak, aby ten od OLT bol prvý.
  //
  // Nie je to len kozmetika zoznamu: connectedCables[0] sa používa aj ako
  // predvolený vstupný kábel pri pridávaní splittra, priechodu a zvaru
  // a connectedCables[1] ako predvolený výstupný. Zoradením sa teda naraz
  // opraví aj poradie v rozbaľovačke, aj to, čo je predvyplnené.
  const { kable: connectedCables, odOltId } = useMemo(
    () => zoradPodlaOlt(
      cables.filter((c) => c.from_pole_id === pole.id || c.to_pole_id === pole.id),
      poles,
      cables,
    ),
    [cables, poles, pole.id],
  );

  // Druhá kazeta na tom istom stĺpiku. Zákaznícka kazeta je virtuálne dvojča
  // stĺpika, takže prípojka môže byť zapísaná v ktorejkoľvek z nich — a technik
  // má vidieť, že je už inde, nech ju nezaloží druhýkrát.
  const druhaKazeta = useMemo(() => {
    if (pole.is_virtual && pole.parent_pole_id) {
      const rodic = poles.find((p) => p.id === pole.parent_pole_id);
      return rodic ? { pole: rodic, popis: 'hlavnej kazete' } : null;
    }
    const zak = poles.find((p) => p.parent_pole_id === pole.id && p.is_virtual);
    return zak ? { pole: zak, popis: 'zákazníckej kazete' } : null;
  }, [poles, pole.id, pole.is_virtual, pole.parent_pole_id]);

  const pripojkyInde = ((druhaKazeta?.pole.cassette as any)?.drops || []) as
    { cable_id: number; fiber: number; label?: string }[];

  // Káble, ktoré cez tento stĺp len PRECHÁDZAJÚ (je na nich podporný).
  // Taký stĺp nemá vlastný kábel, takže editor doteraz ukázal len hlášku
  // „nie je pripojený žiadny kábel" a technik musel ísť späť na mapu,
  // nájsť stĺp, pravý klik, rozdeliť kábel — a znovu sem. Teraz sa to dá
  // priamo odtiaľto.
  const kablePrechadzajuce = useMemo(
    () => cables.filter((c) => (c.support_poles || []).includes(pole.id)),
    [cables, pole.id],
  );

  const rozdelKabelTu = async (kabel: Cable) => {
    if (!window.confirm(
      `Rozdeliť kábel #${kabel.id} na stĺpiku ${pole.name}?\n\n`
      + 'Vzniknú z neho dva káble a tu v kazete priechod, ktorý ich spojí '
      + 'vlákno na vlákno — signál pôjde ďalej presne ako teraz.\n\n'
      + 'Potom už len odbočíš vlákno, ktoré potrebuješ.')) {
      return;
    }
    setDelim(true);
    setError(null);
    try {
      // Pôvodné číslo si necháva polovica od OLT — rovnako ako na mape.
      const odOlt = koniecOdOlt(kabel, poles, cables);
      const keepSide: 'from' | 'to' = odOlt === kabel.to_pole_id ? 'to' : 'from';
      await cablesAPI.splitCable(pole.project_id, kabel.id, {
        pole_id: pole.id,
        keep_side: keepSide,
      });
      await fetchCables(pole.project_id);
      await usePoleStore.getState().fetchPoles(pole.project_id);
      const cerstvy = usePoleStore.getState().poles.find((p) => p.id === pole.id);
      if (cerstvy?.cassette) setCassette(cerstvy.cassette);
      setActiveTab('pass_through');
    } catch (e: any) {
      setError(getErrorMessage(e, 'Kábel sa nepodarilo rozdeliť.'));
    } finally {
      setDelim(false);
    }
  };

  // Zoznam pre VÝSTUPNÉ polia (kam signál pokračuje). Kábel od OLT je tu
  // posledný — do neho signál spravidla nepokračuje, prišiel ním.
  // Zámerne sa NEVYHADZUJE: nadradený splitter reálne posiela svoje výstupy
  // späť do toho istého kábla na ďalšie vlákna (na Stlp 81 je to presne tak),
  // takže tú možnosť musí technik mať — len ju nemá dostať ako prvú.
  const connectedCablesVystup = useMemo(() => {
    if (odOltId === null) return connectedCables;
    const odOlt = connectedCables.filter((c) => c.id === odOltId);
    const ostatne = connectedCables.filter((c) => c.id !== odOltId);
    return [...ostatne, ...odOlt];
  }, [connectedCables, odOltId]);

  // Get ONUs connected to this pole
  const poleONUs = onus.filter(onu => onu.pole_id === pole.id);

  // Helper function to get ONU connected to a specific splitter port
  const getONUForPort = (splitterIndex: number, portNumber: number) => {
    return poleONUs.find(
      onu => onu.splitter_index === splitterIndex && onu.splitter_port === portNumber
    );
  };

  useEffect(() => {
    if (pole.cassette) {
      setCassette(pole.cassette);
    }
  }, [pole]);

  if (!isOpen) return null;

  /** Koľko vlákien má kábel podľa svojho typu (12-fiber, 24-fiber…). */
  const pocetVlakienKabla = (cableId?: number): number => {
    const k = cables.find((c) => c.id === cableId);
    return fiberCountFromCableType(k?.cable_type);
  };

  /**
   * Koľko vlákien môže priechod previesť: menší z oboch káblov. Viac než má
   * tenší z nich sa previesť nedá.
   */
  const pocetVlakienPriechodu = (fromId?: number, toId?: number): number =>
    Math.min(pocetVlakienKabla(fromId), pocetVlakienKabla(toId));

  /**
   * Prispôsobí zoznam vlákien priechodu novému počtu.
   *
   * Vlákna, ktoré sú odbočené (`is_tapped`), sa nezahadzujú ani keď by mali
   * vypadnúť — radšej zostane priechod dlhší než tichá strata zapojenia.
   */
  const prisposobVlakna = (vlakna: any[], pocet: number): any[] => {
    const terajsie = vlakna || [];
    if (terajsie.length === pocet) return terajsie;
    if (terajsie.length < pocet) {
      const doplnene = [...terajsie];
      for (let i = terajsie.length; i < pocet; i++) {
        doplnene.push({ fiber_number: i + 1, is_tapped: false });
      }
      return doplnene;
    }
    return terajsie.filter((v, i) => i < pocet || v?.is_tapped);
  };

  const handleAddPassThrough = () => {
    if (connectedCables.length < 2) {
      setError('Potrebujete aspoň 2 káble pripojené na vytvorenie priechodu (OD → DO)');
      return;
    }

    // Use first two cables as default (user can change later)
    const fromCable = connectedCables[0];
    const toCable = connectedCables[1];

    // Počet vlákien podľa KÁBLOV, nie natvrdo 12 — na 24-vláknovom kábli
    // sa inak dalo prepojiť len prvých dvanásť.
    const pocet = pocetVlakienPriechodu(fromCable.id, toCable.id);
    const newPassThrough = {
      from_cable_id: fromCable.id,
      to_cable_id: toCable.id,
      fibers: Array.from({ length: pocet }, (_, i) => ({
        fiber_number: i + 1,
        is_tapped: false,
      })),
    };

    setCassette({
      ...cassette,
      has_cassette: true,
      pass_through_cables: [...(cassette.pass_through_cables || []), newPassThrough],
    });
    setError(null);
  };

  const handleRemovePassThrough = (index: number) => {
    // Save current state for undo
    const previousPassThroughs = [...(cassette.pass_through_cables || [])];
    const removedItem = previousPassThroughs[index];

    // Remove the item
    const newPassThroughs = [...previousPassThroughs];
    newPassThroughs.splice(index, 1);
    setCassette({
      ...cassette,
      pass_through_cables: newPassThroughs,
    });

    // Show undo toast
    setUndoToast({
      visible: true,
      message: `Priechod káblov bol vymazaný`,
      onUndo: () => {
        setCassette({
          ...cassette,
          pass_through_cables: previousPassThroughs,
        });
        setUndoToast(null);
      },
    });
  };

  const handleToggleFiberTap = (
    passThroughIndex: number,
    fiberIndex: number,
    isTapped: boolean
  ) => {
    const newPassThroughs = [...(cassette.pass_through_cables || [])];
    const pt = newPassThroughs[passThroughIndex];
    const fiber = pt.fibers[fiberIndex];

    if (isTapped) {
      // Enable tap - set default values
      fiber.is_tapped = true;
      // Ktorý koniec priechodu sa zvára — predvolene ten vstupný, ale
      // technik si to vie prepnúť (priechod má dva konce).
      fiber.tap_from_cable_id = pt.from_cable_id;
      fiber.tap_to_cable_id = connectedCables[1]?.id || connectedCables[0].id;
      fiber.tap_to_fiber = fiber.fiber_number; // Same fiber number as source
      fiber.tap_loss_db = 0.1;
    } else {
      // Disable tap
      fiber.is_tapped = false;
      delete fiber.tap_from_cable_id;
      delete fiber.tap_zapisat_zvar;
      delete fiber.tap_to_cable_id;
      delete fiber.tap_to_fiber;
      delete fiber.tap_loss_db;
    }

    setCassette({
      ...cassette,
      pass_through_cables: newPassThroughs,
      splices: zosuladZvarSOdbockou(
        cassette.splices || [],
        fiber.tap_from_cable_id || pt.from_cable_id,
        fiber.fiber_number, fiber),
    });
  };

  const handleUpdateFiberTap = (
    passThroughIndex: number,
    fiberIndex: number,
    field: string,
    value: any
  ) => {
    const newPassThroughs = [...(cassette.pass_through_cables || [])];
    const pt = newPassThroughs[passThroughIndex];
    const fiber = pt.fibers[fiberIndex];
    const povodnyKoniec = fiber.tap_from_cable_id || pt.from_cable_id;
    (fiber as any)[field] = value;

    // Prepnutie zváraného konca musí prepísať aj zvar, ktorý k nemu patrí —
    // inak by v zozname ostal zvar z pôvodného konca.
    let splices = cassette.splices || [];
    if (field === 'tap_from_cable_id') {
      splices = zosuladZvarSOdbockou(
        splices, povodnyKoniec, fiber.fiber_number, { ...fiber, is_tapped: false });
    }

    setCassette({
      ...cassette,
      pass_through_cables: newPassThroughs,
      splices: zosuladZvarSOdbockou(
        splices,
        fiber.tap_from_cable_id || pt.from_cable_id,
        fiber.fiber_number, fiber),
    });
  };

  const handleAddSplice = () => {
    // Need at least 1 cable (can splice within same cable)
    if (connectedCables.length < 1) {
      setError('Potrebujete aspoň 1 kábel pripojený na vytvorenie zvaru');
      return;
    }

    const newSplice = pole.pole_type === 'olt'
      ? {
          from_source_type: 'olt_port' as const,
          from_olt_port: 1,
          to_cable_id: connectedCables[0].id,
          to_fiber: 1,
          loss_db: 0.1,
        }
      : {
          from_source_type: 'cable' as const,
          from_cable_id: connectedCables[0].id,
          from_fiber: 1,
          // Predvolene do INÉHO kábla než ten, ktorým signál prišiel.
          // Keď je na stĺpe len jeden, zostane ten istý (zvar vlákno na vlákno).
          to_cable_id: (connectedCablesVystup[0] || connectedCables[0]).id,
          to_fiber: 2, // Different fiber
          loss_db: 0.1,
        };

    setCassette({
      ...cassette,
      has_cassette: true,
      splices: [...(cassette.splices || []), newSplice],
    });
    setError(null);
  };

  const handleRemoveSplice = (index: number) => {
    // Save current state for undo
    const previousSplices = [...(cassette.splices || [])];
    const previousPts = JSON.parse(JSON.stringify(cassette.pass_through_cables || []));
    const removedItem = previousSplices[index];

    // Remove the item
    const newSplices = [...previousSplices];
    newSplices.splice(index, 1);

    // Zvar vznikol z odbočky — odbočka ostáva (vlákno pokojne môže byť
    // vybraté a voľné), len sa odškrtne „Zapísať zvar", nech sa nevráti.
    let newPts = cassette.pass_through_cables || [];
    let ajOdbocka = false;
    if (removedItem?.z_odbocky && removedItem.from_cable_id && removedItem.from_fiber) {
      const kopia = JSON.parse(JSON.stringify(newPts));
      for (const pt of kopia) {
        if (pt.from_cable_id !== removedItem.from_cable_id
            && pt.to_cable_id !== removedItem.from_cable_id) continue;
        const f = (pt.fibers || []).find((x: any) => x.fiber_number === removedItem.from_fiber);
        if (f && f.tap_zapisat_zvar) {
          delete f.tap_zapisat_zvar;
          ajOdbocka = true;
        }
      }
      if (ajOdbocka) newPts = kopia;
    }

    setCassette({
      ...cassette,
      splices: newSplices,
      pass_through_cables: newPts,
    });

    // Show undo toast
    setUndoToast({
      visible: true,
      message: ajOdbocka
        ? `Zvar #${index + 1} vymazaný — vlákno ostáva vybraté z priechodu, len bez zvaru`
        : `Zvar #${index + 1} bol vymazaný`,
      onUndo: () => {
        setCassette({
          ...cassette,
          splices: previousSplices,
          pass_through_cables: previousPts,
        });
        setUndoToast(null);
      },
    });
  };

  const handleUpdateSplice = (index: number, field: string, value: any) => {
    const newSplices = [...(cassette.splices || [])];
    newSplices[index] = {
      ...newSplices[index],
      [field]: value,
    };
    setCassette({
      ...cassette,
      splices: newSplices,
    });
  };

  const handleAddSplitter = () => {
    // Need at least 1 cable (can use same cable for input and outputs)
    if (connectedCables.length < 1) {
      setError('Potrebujete aspoň 1 kábel pripojený na vytvorenie splittra');
      return;
    }

    // Use first cable for both input and outputs by default
    const outputCable = connectedCables[0];

    // Find first available fiber for input (not used by other splitters/splices)
    let firstAvailableFiber = 1;
    for (let fiberNum = 1; fiberNum <= 12; fiberNum++) {
      const isUsed = isFiberUsed(connectedCables[0].id, fiberNum, 'splitter');
      const cassetteUsage = isFiberUsedInCurrentCassette(connectedCables[0].id, fiberNum);
      if (!isUsed && !cassetteUsage.isUsed) {
        firstAvailableFiber = fiberNum;
        break;
      }
    }

    const newSplitter = pole.pole_type === 'olt'
      ? {
          type: '1:2' as const,
          input_source_type: 'olt_port' as const,
          input_olt_port: 1,
          outputs: [
            { cable_id: undefined, fiber: undefined }, // Empty output
            { cable_id: undefined, fiber: undefined }, // Empty output
          ],
        }
      : {
          type: '1:2' as const,
          input_source_type: 'cable' as const,
          input_cable_id: connectedCables[0].id,
          input_fiber: firstAvailableFiber, // Use first available fiber
          outputs: [
            { cable_id: undefined, fiber: undefined }, // Empty output
            { cable_id: undefined, fiber: undefined }, // Empty output
          ],
        };

    setCassette({
      ...cassette,
      has_cassette: true,
      splitters: [...(cassette.splitters || []), newSplitter],
    });
    setError(null);
  };

  const handleRemoveSplitter = (index: number) => {
    // Check if splitter has connected ONUs
    const splitter = (cassette.splitters || [])[index];
    const connectedONUs = poleONUs.filter(onu => onu.splitter_index === index);

    if (connectedONUs.length > 0) {
      const onuNames = connectedONUs.map(onu => onu.name).join(', ');
      if (!window.confirm(
        `⚠️ POZOR: Tento splitter má pripojené ${connectedONUs.length} ONU(s): ${onuNames}\n\n` +
        `Vymazaním splittra sa NEVYMAŽÚ ONU, ale stratia prepojenie!\n\n` +
        `Naozaj chcete pokračovať?`
      )) {
        return;
      }
    }

    // Save current state for undo
    const previousSplitters = [...(cassette.splitters || [])];

    // Remove the item
    const newSplitters = [...previousSplitters];
    newSplitters.splice(index, 1);
    setCassette({
      ...cassette,
      splitters: newSplitters,
    });

    // Show undo toast
    setUndoToast({
      visible: true,
      message: `Splitter #${index + 1} (${splitter?.type || 'unknown'}) bol vymazaný`,
      onUndo: () => {
        setCassette({
          ...cassette,
          splitters: previousSplitters,
        });
        setUndoToast(null);
      },
    });
  };

  const handleUpdateSplitter = (index: number, field: string, value: any) => {
    const newSplitters = [...(cassette.splitters || [])];
    
    if (field === 'type') {
      // Update splitter type and adjust outputs (počet z katalógu splitrov)
      const outputCount = getSplitterOutputs(value);
      const currentOutputs = newSplitters[index].outputs || [];
      const newOutputs = [];

      for (let i = 0; i < outputCount; i++) {
        // Keep existing output if available, otherwise create empty output
        newOutputs.push(
          currentOutputs[i] || {
            cable_id: undefined,
            fiber: undefined,
          }
        );
      }

      newSplitters[index] = {
        ...newSplitters[index],
        type: value,
        outputs: newOutputs,
      };
    } else {
      newSplitters[index] = {
        ...newSplitters[index],
        [field]: value,
      };
    }
    
    setCassette({
      ...cassette,
      splitters: newSplitters,
    });
  };

  const handleUpdateSplitterOutput = (
    splitterIndex: number,
    outputIndex: number,
    field: string,
    value: any
  ) => {
    const newSplitters = [...(cassette.splitters || [])];
    const newOutputs = [...newSplitters[splitterIndex].outputs];
    newOutputs[outputIndex] = {
      ...newOutputs[outputIndex],
      [field]: value,
    };
    newSplitters[splitterIndex] = {
      ...newSplitters[splitterIndex],
      outputs: newOutputs,
    };
    setCassette({
      ...cassette,
      splitters: newSplitters,
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // updatePole already fetches updated poles from color propagation
      await updatePole(pole.project_id, pole.id, { cassette });

      // Refresh cables to update fiber assignments
      await fetchCables(pole.project_id);

      // After updatePole completes (including fetching updated poles),
      // get the updated pole from store
      const freshPoles = usePoleStore.getState().poles;
      const updatedPole = freshPoles.find(p => p.id === pole.id);
      if (updatedPole && updatedPole.cassette) {
        console.log('[CassetteEditor] Updating local cassette with fresh data from store');
        setCassette(updatedPole.cassette);
      }

      // Don't close - let user continue editing
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa uložiť kazetu.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async () => {
    setLoading(true);
    setError(null);

    try {
      // updatePole already fetches updated poles from color propagation
      await updatePole(pole.project_id, pole.id, { cassette });
      // Refresh cables to update fiber assignments
      await fetchCables(pole.project_id);
      onClose();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa uložiť kazetu.'));
    } finally {
      setLoading(false);
    }
  };

  /** Štítok pri kábli, ktorý prichádza od OLT — nech je vidno, ktorý to je. */
  const oznacOlt = (cableId: number) => (cableId === odOltId ? ' ↓ od OLT' : '');

  const getCableName = (cableId: number) => {
    const cable = cables.find((c) => c.id === cableId);
    if (!cable) return `Cable #${cableId}`;

    // Get pole names
    const fromPole = poles.find((p) => p.id === cable.from_pole_id);
    const toPole = poles.find((p) => p.id === cable.to_pole_id);

    const fromName = fromPole?.name || `Pole #${cable.from_pole_id}`;
    const toName = toPole?.name || `Pole #${cable.to_pole_id}`;

    return `${fromName} → ${toName} (${cable.length_meters}m)`;
  };

  // Get fiber count from cable
  const getCableFiberCount = (cableId: number): number => {
    const cable = cables.find((c) => c.id === cableId);
    if (!cable) return 12;

    // Try to extract from cable_type (e.g., "24-fiber" -> 24)
    const match = cable.cable_type?.match(/(\d+)/);
    if (match) return parseInt(match[1]);

    // Fallback to fiber_assignments length
    return cable.fiber_assignments?.length || 12;
  };

  // Get buffer configuration for a cable
  const getCableBufferConfig = (cableId: number) => {
    const cable = cables.find((c) => c.id === cableId);
    if (!cable) return null;

    // If cable has buffer_config, use it
    if (cable.buffer_config) return cable.buffer_config;

    // Otherwise, return null (no buffers)
    return null;
  };

  const getFiberColor = (cableId: number, fiberNum: number) => {
    const bufferConfig = getCableBufferConfig(cableId);
    if (!bufferConfig) {
      // No buffer config - use simple fiber colors (24-fiber cable logic)
      return getFiberColorInBuffer(fiberNum, 24);
    }
    const fiberInfo = getFiberColorName(fiberNum, bufferConfig);
    return fiberInfo.colorName;
  };

  // Get fiber display name with buffer info
  const signalVlakna = (cableId: number | undefined, fiberNum: number): string => {
    if (!cableId || !fiberNum) return '';
    const kluc = `${cableId}:${fiberNum}`;
    const ulozene = signalCache.get(kluc);
    if (ulozene !== undefined) return ulozene;

    let popis = '';
    try {
      const cesta = traceOpticalPath(cableId, fiberNum, poles, cables);
      if (cesta.found && cesta.isValid) {
        const p = cesta.finalPower;
        // Rovnaké hranice, aké používa FiberSignalIndicator
        const ikona = p >= -8 ? '🟢' : p >= -15 ? '🟡' : p >= -25 ? '🟠' : p >= -28 ? '🔴' : '⛔';
        popis = ` (${ikona} ${p.toFixed(1).replace('.', ',')} dBm)`;
      } else {
        popis = ' (⚫ bez signálu)';
      }
    } catch {
      popis = '';
    }
    signalCache.set(kluc, popis);
    return popis;
  };

  const getFiberDisplayNameForCable = (cableId: number, fiberNum: number): string => {
    const bufferConfig = getCableBufferConfig(cableId);
    const zaklad = bufferConfig
      ? getFiberDisplayName(fiberNum, bufferConfig)
      // Bez konfigurácie bufrov vieme povedať len číslo a farbu vlákna
      : `Vlákno ${fiberNum} (${getFiberColorInBuffer(fiberNum, 24)})`;
    return zaklad + signalVlakna(cableId, fiberNum);
  };

  /**
   * Rozbaľovačka vlákien kábla s buframi: možnosti zoskupí po bufroch
   * (<optgroup> „Buffer 1 (Modrá)“), aby 24 vlákien nebolo jedným dlhým
   * zoznamom. `moznosti` sú vlákna 1..N v poradí. Kábel bez buffer_config
   * alebo s jediným bufrom ostáva ako doteraz.
   */
  const poBufroch = (cableId: number | undefined, moznosti: React.ReactElement[]) => {
    const bc = cableId ? getCableBufferConfig(cableId) : null;
    if (!bc || !bc.buffer_size || moznosti.length <= bc.buffer_size) return moznosti;
    const skupiny: React.ReactElement[] = [];
    for (let i = 0; i < moznosti.length; i += bc.buffer_size) {
      const n = i / bc.buffer_size + 1;
      const farba = bc.buffers.find((b) => b.index === n)?.color;
      skupiny.push(
        <optgroup key={`buffer-${n}`} label={`Buffer ${n}${farba ? ` (${farba})` : ''}`}>
          {moznosti.slice(i, i + bc.buffer_size)}
        </optgroup>,
      );
    }
    return skupiny;
  };

  type PriechodVlakno = NonNullable<CassetteConfig['pass_through_cables']>[number]['fibers'][number];

  /**
   * Vlákna priechodu po bufroch v rozbaľovacích sekciách. Rozbalený je na
   * začiatku len buffer, v ktorom je niečo odbočené; ostatné sú zbalené,
   * kým ich technik neotvorí. Bez bufrov sa vypíšu ako doteraz.
   */
  const poBufrochPriechod = (
    ptIndex: number,
    passThrough: { from_cable_id: number; fibers: PriechodVlakno[] },
    vlakno: (fiber: PriechodVlakno, fiberIndex: number) => React.ReactNode,
  ) => {
    const bc = getCableBufferConfig(passThrough.from_cable_id);
    if (!bc || !bc.buffer_size || passThrough.fibers.length <= bc.buffer_size) {
      return passThrough.fibers.map(vlakno);
    }
    const skupiny = new Map<number, { fiber: PriechodVlakno; fiberIndex: number }[]>();
    passThrough.fibers.forEach((fiber, fiberIndex) => {
      const n = Math.ceil(fiber.fiber_number / bc.buffer_size);
      if (!skupiny.has(n)) skupiny.set(n, []);
      skupiny.get(n)!.push({ fiber, fiberIndex });
    });
    return Array.from(skupiny.entries()).sort((a, b) => a[0] - b[0]).map(([n, polozky]) => {
      const odbocene = polozky.filter((x) => x.fiber.is_tapped).length;
      const farba = bc.buffers.find((b) => b.index === n)?.color;
      const kluc = `${ptIndex}-${n}`;
      return (
        <details
          key={`buffer-${n}`}
          className="buffer-skupina"
          open={otvoreneBufre[kluc] ?? odbocene > 0}
          onToggle={(e) => {
            const otvorene = (e.currentTarget as HTMLDetailsElement).open;
            setOtvoreneBufre((s) => (s[kluc] === otvorene ? s : { ...s, [kluc]: otvorene }));
          }}
        >
          <summary>
            Buffer {n}
            {farba && (
              <span className="fiber-color-badge" style={getFiberColorStyle(farba)}>{farba}</span>
            )}
            <span className="buffer-skupina-pocet">
              {polozky.length} vlákien{odbocene > 0 ? ` · odbočené ${odbocene}` : ''}
            </span>
          </summary>
          <div className="buffer-skupina-vlakna">
            {polozky.map(({ fiber, fiberIndex }) => vlakno(fiber, fiberIndex))}
          </div>
        </details>
      );
    });
  };

  const getFiberColorStyle = (color: string): React.CSSProperties => {
    return getFiberBadgeStyle(color);
  };

  // Check if fiber is used in current cassette (by splitters, splices, etc.)
  const isFiberUsedInCurrentCassette = (
    cableId: number,
    fiberNum: number,
    excludeSplitterIndex?: number,
    excludeSpliceIndex?: number,
    excludeOutputIndex?: number,
    excludePassThroughIndex?: number
  ): { isUsed: boolean; usedBy: string } => {
    // Check if used as splitter input
    const splitters = cassette.splitters || [];
    for (let i = 0; i < splitters.length; i++) {
      const splitter = splitters[i];

      // Check splitter input (skip if this is the current splitter being edited)
      if (i !== excludeSplitterIndex) {
        if (splitter.input_source_type === 'cable' &&
            splitter.input_cable_id === cableId &&
            splitter.input_fiber === fiberNum) {
          return { isUsed: true, usedBy: `Splitter #${i + 1} INPUT` };
        }
      }

      // Check if used as splitter output
      const outputs = splitter.outputs || [];
      for (let j = 0; j < outputs.length; j++) {
        // Skip current output if we're checking within the same splitter
        if (i === excludeSplitterIndex && j === excludeOutputIndex) {
          continue;
        }

        const output = outputs[j];
        if (output.cable_id === cableId && output.fiber === fiberNum) {
          return { isUsed: true, usedBy: `Splitter #${i + 1} Output #${j + 1}` };
        }
      }
    }

    // Check if used in splices
    const splices = cassette.splices || [];
    for (let i = 0; i < splices.length; i++) {
      if (i === excludeSpliceIndex) continue; // Skip current splice

      const splice = splices[i];

      if (splice.from_cable_id === cableId && splice.from_fiber === fiberNum) {
        return { isUsed: true, usedBy: `Splice #${i + 1} FROM` };
      }
      if (splice.to_cable_id === cableId && splice.to_fiber === fiberNum) {
        return { isUsed: true, usedBy: `Splice #${i + 1} TO` };
      }
    }

    // Check if used in pass-through (but only if NOT tapped - tapped fibers are available)
    const passThroughCables = cassette.pass_through_cables || [];
    for (let i = 0; i < passThroughCables.length; i++) {
      // Skip current pass-through being edited
      if (i === excludePassThroughIndex) continue;

      const pt = passThroughCables[i];

      // Check both from_cable_id and to_cable_id
      if (pt.from_cable_id === cableId || pt.to_cable_id === cableId) {
        const ptFiber = pt.fibers?.find((f: any) => f.fiber_number === fiberNum);
        if (ptFiber && !ptFiber.is_tapped) {
          // Fiber is in pass-through but NOT tapped = considered "used" (not available for splice/splitter)
          return { isUsed: true, usedBy: `Pass-through #${i + 1} (not tapped)` };
        }
        // If ptFiber.is_tapped === true, fiber is available for use
      }
    }

    return { isUsed: false, usedBy: '' };
  };

  /**
   * Je vlákno v priechode a ešte neodbočené?
   *
   * Taká kombinácia je dnes v zozname zablokovaná („Pass-through #1 (not
   * tapped)") a technik musí ísť do záložky Priechod, nájsť vlákno,
   * zaškrtnúť Tap a nastaviť cieľ. Keď to vieme zistiť, vieme mu to aj
   * ponúknuť jedným klikom.
   */
  const priechodBezOdbocky = (
    cableId?: number | null,
    fiberNum?: number | null,
  ): { ptIndex: number; fiberIndex: number } | null => {
    if (!cableId || !fiberNum) return null;
    const pts = cassette.pass_through_cables || [];
    for (let i = 0; i < pts.length; i++) {
      const pt = pts[i];
      if (pt.from_cable_id !== cableId && pt.to_cable_id !== cableId) continue;
      const fi = (pt.fibers || []).findIndex(
        (f: any) => f.fiber_number === fiberNum && !f.is_tapped);
      if (fi >= 0) return { ptIndex: i, fiberIndex: fi };
    }
    return null;
  };

  /**
   * Odbočí vlákno z priechodu. Zvar sa NEMAŽE — on tam fyzicky je a musí
   * byť v dokumentácii aj v montážnom checkliste, inak by technik prišiel
   * k stĺpiku a o tej robote by sa nikde nedočítal. Odbočka v priechode
   * hovorí „toto vlákno tu končí", zvar hovorí „a je zvarené sem".
   */
  const odbocVlakno = (
    cableId: number,
    fiberNum: number,
    cielKabel: number,
    cielVlakno: number,
    soZvarom = false,
  ): boolean => {
    const najdene = priechodBezOdbocky(cableId, fiberNum);
    if (!najdene) return false;
    const pts = [...(cassette.pass_through_cables || [])];
    const pt = { ...pts[najdene.ptIndex] };
    const vlakna = [...(pt.fibers || [])];
    vlakna[najdene.fiberIndex] = {
      ...vlakna[najdene.fiberIndex],
      is_tapped: true,
      tap_from_cable_id: cableId,
      tap_zapisat_zvar: soZvarom || undefined,
      tap_to_cable_id: cielKabel,
      tap_to_fiber: cielVlakno,
      tap_loss_db: vlakna[najdene.fiberIndex].tap_loss_db || 0.1,
    };
    pt.fibers = vlakna;
    pts[najdene.ptIndex] = pt;
    setCassette({ ...cassette, pass_through_cables: pts });
    return true;
  };

  /** Pri zvare: odbočí vlákno a zvar nechá zapísaný tak, ako je. */
  const odbocPreZvar = (spliceIndex: number) => {
    const sp = (cassette.splices || [])[spliceIndex];
    if (!sp || !sp.from_cable_id || !sp.from_fiber || !sp.to_cable_id || !sp.to_fiber) return;
    // Tu zvar už existuje (technik ho práve písal), takže odbočka sa k nemu
    // rovno prihlási — nech sa držia v páre.
    if (!odbocVlakno(sp.from_cable_id, sp.from_fiber, sp.to_cable_id, sp.to_fiber, true)) return;
    setOznam(
      `Vlákno ${getFiberDisplayNameForCable(sp.from_cable_id, sp.from_fiber)} `
      + `som vybral z priechodu. Zvar #${spliceIndex + 1} ostáva zapísaný — `
      + 'pôjde do dokumentácie aj do montážneho checklistu. Nezabudni uložiť kazetu.');
  };

  /**
   * Pri splittri: vlákno sa z priechodu vyberie a ide do splittra.
   * Odbočka ukazuje sama na seba (ten istý kábel a vlákno) — tak to má
   * zapísaných aj 128 splittrov, ktoré sú v projektoch dnes. Samotné
   * zvarenie na pigtail splittra dokumentuje vstup splittra, ten už
   * v checkliste úlohou je.
   */
  const odbocPreSplitter = (cableId: number, fiberNum: number) => {
    const najdene = priechodBezOdbocky(cableId, fiberNum);
    if (!najdene) return;

    const pts = [...(cassette.pass_through_cables || [])];
    const pt = { ...pts[najdene.ptIndex] };
    const vlakna = [...(pt.fibers || [])];
    vlakna[najdene.fiberIndex] = {
      ...vlakna[najdene.fiberIndex],
      is_tapped: true,
      tap_from_cable_id: cableId,
      tap_to_cable_id: cableId,
      tap_to_fiber: fiberNum,
      tap_loss_db: vlakna[najdene.fiberIndex].tap_loss_db || 0.1,
    };
    pt.fibers = vlakna;
    pts[najdene.ptIndex] = pt;

    // Zvar sa sem NEPÍŠE. Zvarenie vlákna na pigtail splittra drží vstup
    // splittra — je to jeden fyzický úkon a v montážnom checkliste je preň
    // úloha „Zvariť … na vstup splittra". Druhý zápis do zoznamu zvarov by
    // znamenal tú istú robotu dvakrát.
    setCassette({ ...cassette, pass_through_cables: pts });
    setOznam(
      `Vlákno ${getFiberDisplayNameForCable(cableId, fiberNum)} `
      + 'som vybral z priechodu, takže ho splitter môže brať ako vstup. '
      + 'Zvarenie na splitter je v checkliste úlohou pri vstupe splittra. '
      + 'Nezabudni uložiť kazetu.');
  };

  const isFiberUsed = (
    cableId: number,
    fiberNum: number,
    context: 'passthrough' | 'splice' | 'splitter' = 'splice',
    excludePassThroughIndex?: number
  ): boolean => {
    // Check only current cassette - is fiber used in splice/splitter/pass-through in THIS pole?
    const currentCassetteUsage = isFiberUsedInCurrentCassette(
      cableId,
      fiberNum,
      undefined, // excludeSplitterIndex
      undefined, // excludeSpliceIndex
      undefined, // excludeOutputIndex
      excludePassThroughIndex
    );
    return currentCassetteUsage.isUsed;
  };

  const getFiberUsedBy = (
    cableId: number,
    fiberNum: number,
    context: 'passthrough' | 'splice' | 'splitter' = 'splice',
    excludePassThroughIndex?: number
  ): string | null => {
    // For pass-through context, check only current cassette
    if (context === 'passthrough') {
      const cassetteUsage = isFiberUsedInCurrentCassette(
        cableId,
        fiberNum,
        undefined, // excludeSplitterIndex
        undefined, // excludeSpliceIndex
        undefined, // excludeOutputIndex
        excludePassThroughIndex
      );
      return cassetteUsage.isUsed ? cassetteUsage.usedBy : null;
    }

    const cable = cables.find((c) => c.id === cableId);
    if (!cable || !cable.fiber_assignments) return null;

    const fiber = cable.fiber_assignments.find((f) => f.fiber_number === fiberNum);

    // FIRST: Check if fiber is tapped in pass-through in CURRENT pole
    // If tapped, it's available for use in splice/splitter
    const passThroughCables = cassette.pass_through_cables || [];
    for (const pt of passThroughCables) {
      if (pt.from_cable_id === cableId || pt.to_cable_id === cableId) {
        const ptFiber = pt.fibers?.find((f: any) => f.fiber_number === fiberNum);
        if (ptFiber && ptFiber.is_tapped) {
          // Fiber is tapped in pass-through → available for splice/splitter
          return null;
        }
      }
    }

    // Check if used by different pole
    if (fiber && fiber.is_used && fiber.used_by_pole_id !== pole.id) {
      const usedByPole = poles.find((p) => p.id === fiber.used_by_pole_id);

      // If used by OLT pole, show as available for tapping
      if (usedByPole && usedByPole.pole_type === 'olt') {
        return null; // Don't show as "used" - it's available for tapping
      }

      // Check if fiber is used as OUTPUT in the other pole
      // If yes, it's available as INPUT in current pole (via cable)
      if (usedByPole && usedByPole.cassette) {
        // Check splitter outputs
        if (usedByPole.cassette.splitters) {
          for (const splitter of usedByPole.cassette.splitters) {
            const outputMatch = splitter.outputs?.some(
              (out: any) => out.cable_id === cableId && out.fiber === fiberNum
            );
            if (outputMatch) {
              // Fiber is OUTPUT from splitter in other pole → available as INPUT here
              return null;
            }
          }
        }

        // Check splice TO (output)
        if (usedByPole.cassette.splices) {
          for (const splice of usedByPole.cassette.splices) {
            if (splice.to_cable_id === cableId && splice.to_fiber === fiberNum) {
              // Fiber is OUTPUT from splice in other pole → available as INPUT here
              return null;
            }
          }
        }

        // Check pass-through TO (output)
        if (usedByPole.cassette.pass_through_cables) {
          for (const pt of usedByPole.cassette.pass_through_cables) {
            if (pt.to_cable_id === cableId) {
              const ptFiber = pt.fibers?.find((f: any) => f.fiber_number === fiberNum);
              if (ptFiber) {
                // Fiber is OUTPUT from pass-through in other pole → available as INPUT here
                return null;
              }
            }
            // Check pass-through FROM (input) - if tapped, it's being used in that pole
            // but the fiber is still available for use as OUTPUT in upstream pole
            if (pt.from_cable_id === cableId) {
              const ptFiber = pt.fibers?.find((f: any) => f.fiber_number === fiberNum);
              if (ptFiber && ptFiber.is_tapped) {
                // Fiber is INPUT to pass-through and tapped → available as OUTPUT from upstream pole
                return null;
              }
            }
            // Check if fiber is tapped TO this cable
            for (const fiber of pt.fibers || []) {
              if (fiber.is_tapped && fiber.tap_to_cable_id === cableId && fiber.tap_to_fiber === fiberNum) {
                // Fiber is tapped TO this cable → available as OUTPUT from current pole
                return null;
              }
            }
          }
        }
      }

      return `Pole #${fiber.used_by_pole_id}`;
    }

    // Check if in pass-through (not tapped) on THIS pole
    // (passThroughCables already declared above)
    for (const pt of passThroughCables) {
      // Check both from_cable_id and to_cable_id
      if (pt.from_cable_id === cableId || pt.to_cable_id === cableId) {
        const ptFiber = pt.fibers.find((f) => f.fiber_number === fiberNum);
        if (ptFiber && !ptFiber.is_tapped) {
          return 'Priechod (neodvetvený)';
        }
      }
    }

    return null;
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog cassette-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📦 Editor kazety - {pole.name}</h2>
          <button className="dialog-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {error && <div className="dialog-error">{error}</div>}

          {oznam && (
            <div className="cassette-oznam">
              ✅ {oznam}
              <button className="btn-remove" onClick={() => setOznam(null)}>✕</button>
            </div>
          )}

          {connectedCables.length === 0 && kablePrechadzajuce.length === 0 && (
            <div className="cassette-warning">
              ⚠️ Na tomto stĺpiku nie je pripojený žiadny kábel. Najprv nejaký pripojte.
            </div>
          )}

          {/* Ponuka na rozdelenie sa kedysi viazala na to, že na stĺpiku nie je
              ŽIADNY kábel. Lenže zákaznícka kazeta pripojí virtuálny kábel, takže
              hneď ako niekto takú kazetu založil, podmienka prestala platiť a
              ponuka zmizla — kábel sa potom nedalo rozdeliť odnikiaľ (nájdené na
              Stlp 81 v projekte Ladice-Nevernet). Teraz stačí, že tadiaľto nejaký
              kábel prechádza. */}
          {kablePrechadzajuce.length > 0 && (
            <div className="cassette-warning">
              <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                ✂️ Tadiaľto kábel len prechádza — stĺpik je naň podporný
              </div>
              <p style={{ margin: '0 0 12px', fontSize: '13px', lineHeight: 1.5 }}>
                {connectedCables.length === 0 ? (
                  <>
                    Kazeta potrebuje kábel prerezaný. Rozdelím ho tu na dva a rovno
                    vytvorím priechod, takže signál pôjde ďalej ako doteraz a ty už
                    len odbočíš vlákno, ktoré potrebuješ.
                  </>
                ) : (
                  <>
                    Na vlákna tohto kábla sa tu zatiaľ dostať nedá — nie je
                    prerezaný. Ak z neho potrebuješ odbočiť, rozdelím ho tu na dva
                    a rovno vytvorím priechod, takže signál pôjde ďalej ako doteraz.
                  </>
                )}
              </p>
              {kablePrechadzajuce.map((k) => (
                <button
                  key={k.id}
                  className="btn-primary"
                  style={{ width: '100%', marginBottom: '8px' }}
                  disabled={delim}
                  onClick={() => rozdelKabelTu(k)}
                >
                  {delim ? 'Delím…' : `✂️ Rozdeliť ${getCableName(k.id)} tu a pridať priechod`}
                </button>
              ))}
            </div>
          )}

          {connectedCables.length > 0 && (
            <>
              <div className="cassette-info">
                <p>Pripojené káble: {connectedCables.length}</p>
              </div>

              <div className="cassette-tabs">
                <button
                  className={`cassette-tab ${activeTab === 'pass_through' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pass_through')}
                >
                  ➡️ Priechod ({cassette.pass_through_cables?.length || 0})
                </button>
                <button
                  className={`cassette-tab ${activeTab === 'splices' ? 'active' : ''}`}
                  onClick={() => setActiveTab('splices')}
                >
                  🔗 Zvary ({cassette.splices?.length || 0})
                  {(cassette.drops?.length || 0) > 0 && (
                    <span className="cassette-tab-badge">
                      + {cassette.drops!.length} prípojky
                    </span>
                  )}
                </button>
                <button
                  className={`cassette-tab ${activeTab === 'splitters' ? 'active' : ''}`}
                  onClick={() => setActiveTab('splitters')}
                >
                  🔀 Splittre ({cassette.splitters?.length || 0})
                </button>
              </div>

              {activeTab === 'pass_through' && (
                <div className="cassette-tab-content">
                  <div className="cassette-section-header">
                    <h3>Priechod káblov</h3>
                    <button className="btn-add" onClick={handleAddPassThrough}>
                      + Pridať priechod
                    </button>
                  </div>

                  {cassette.pass_through_cables && cassette.pass_through_cables.length > 0 ? (
                    <div className="passthrough-list">
                      {cassette.pass_through_cables.map((passThrough, ptIndex) => (
                        <div key={ptIndex} className="passthrough-item">
                          <div className="passthrough-header">
                            <span>
                              Priechod: {getCableName(passThrough.from_cable_id)} → {getCableName(passThrough.to_cable_id)}
                            </span>
                            <button
                              className="btn-remove"
                              onClick={() => handleRemovePassThrough(ptIndex)}
                            >
                              ✕
                            </button>
                          </div>

                          <div className="passthrough-cable-selects">
                            <div className="form-group">
                              <label>FROM Cable (vstupný):</label>
                              <select
                                value={passThrough.from_cable_id}
                                onChange={(e) => {
                                  const updated = [...(cassette.pass_through_cables || [])];
                                  const novy = parseInt(e.target.value);
                                  updated[ptIndex] = {
                                    ...updated[ptIndex],
                                    from_cable_id: novy,
                                    fibers: prisposobVlakna(
                                      updated[ptIndex].fibers,
                                      pocetVlakienPriechodu(novy, updated[ptIndex].to_cable_id)),
                                  };
                                  setCassette({ ...cassette, pass_through_cables: updated });
                                }}
                              >
                                {connectedCables.map((cable) => (
                                  <option key={cable.id} value={cable.id}>
                                    {getCableName(cable.id)}{oznacOlt(cable.id)}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="form-group">
                              <label>TO Cable (výstupný):</label>
                              <select
                                value={passThrough.to_cable_id}
                                onChange={(e) => {
                                  const updated = [...(cassette.pass_through_cables || [])];
                                  const novy = parseInt(e.target.value);
                                  updated[ptIndex] = {
                                    ...updated[ptIndex],
                                    to_cable_id: novy,
                                    fibers: prisposobVlakna(
                                      updated[ptIndex].fibers,
                                      pocetVlakienPriechodu(updated[ptIndex].from_cable_id, novy)),
                                  };
                                  setCassette({ ...cassette, pass_through_cables: updated });
                                }}
                              >
                                {connectedCablesVystup.map((cable) => (
                                  <option key={cable.id} value={cable.id}>
                                    {getCableName(cable.id)}{oznacOlt(cable.id)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="passthrough-fibers">
                            <p className="passthrough-hint">
                              ℹ️ Kábel prechádza cez stĺp. Vlákna z FROM cable pokračujú do TO cable. Môžeš "odtapovať" jednotlivé vlákna.
                            </p>
                            {poBufrochPriechod(ptIndex, passThrough, (fiber, fiberIndex) => (
                              <div key={fiberIndex} className="passthrough-fiber">
                                <div className="fiber-info">
                                  <span className="fiber-number">
                                    {getFiberDisplayNameForCable(passThrough.from_cable_id, fiber.fiber_number)}
                                  </span>
                                  <span
                                    className="fiber-color-badge"
                                    style={getFiberColorStyle(
                                      getFiberColor(passThrough.from_cable_id, fiber.fiber_number)
                                    )}
                                  >
                                    {getFiberColor(passThrough.from_cable_id, fiber.fiber_number)}
                                  </span>
                                </div>

                                <div className="fiber-tap-toggle">
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={fiber.is_tapped}
                                      disabled={isFiberUsed(passThrough.from_cable_id, fiber.fiber_number, 'passthrough', ptIndex)}
                                      onChange={(e) =>
                                        handleToggleFiberTap(ptIndex, fiberIndex, e.target.checked)
                                      }
                                    />
                                    <span>
                                      Tap (odtapovať)
                                      {isFiberUsed(passThrough.from_cable_id, fiber.fiber_number, 'passthrough', ptIndex) && (
                                        <span style={{ color: '#e74c3c', marginLeft: '8px' }}>
                                          - {getFiberUsedBy(passThrough.from_cable_id, fiber.fiber_number, 'passthrough', ptIndex)}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                </div>

                                {fiber.is_tapped && (
                                  <div className="fiber-tap-config">
                                    {passThrough.from_cable_id !== passThrough.to_cable_id && (
                                      <div className="tap-pole">
                                        <label>Zvárať koniec</label>
                                        <select
                                          value={fiber.tap_from_cable_id || passThrough.from_cable_id}
                                          onChange={(e) =>
                                            handleUpdateFiberTap(
                                              ptIndex, fiberIndex,
                                              'tap_from_cable_id',
                                              parseInt(e.target.value))
                                          }
                                        >
                                          <option value={passThrough.from_cable_id}>
                                            {getCableName(passThrough.from_cable_id)}{oznacOlt(passThrough.from_cable_id)}
                                          </option>
                                          <option value={passThrough.to_cable_id}>
                                            {getCableName(passThrough.to_cable_id)}{oznacOlt(passThrough.to_cable_id)}
                                          </option>
                                        </select>
                                      </div>
                                    )}

                                    <div className="tap-pole">
                                    <label>Odbočiť do kábla</label>
                                    <select
                                      value={fiber.tap_to_cable_id}
                                      onChange={(e) =>
                                        handleUpdateFiberTap(
                                          ptIndex,
                                          fiberIndex,
                                          'tap_to_cable_id',
                                          parseInt(e.target.value)
                                        )
                                      }
                                    >
                                      {connectedCablesVystup.map((cable) => (
                                        <option key={cable.id} value={cable.id}>
                                          → {getCableName(cable.id)}{oznacOlt(cable.id)}
                                        </option>
                                      ))}
                                    </select>
                                    </div>

                                    <div className="tap-pole">
                                    <label>Na vlákno</label>
                                    <select
                                      value={fiber.tap_to_fiber}
                                      onChange={(e) =>
                                        handleUpdateFiberTap(
                                          ptIndex,
                                          fiberIndex,
                                          'tap_to_fiber',
                                          parseInt(e.target.value)
                                        )
                                      }
                                    >
                                      {poBufroch(fiber.tap_to_cable_id, [...Array(getCableFiberCount(fiber.tap_to_cable_id || 0))].map((_, i) => {
                                        const fiberNum = i + 1;
                                        const isUsed = isFiberUsed(fiber.tap_to_cable_id || 0, fiberNum, 'passthrough');
                                        const usedBy = getFiberUsedBy(fiber.tap_to_cable_id || 0, fiberNum, 'passthrough');
                                        const fiberDisplayName = getFiberDisplayNameForCable(fiber.tap_to_cable_id || 0, fiberNum);
                                        return (
                                          <option
                                            key={fiberNum}
                                            value={fiberNum}
                                            disabled={isUsed}
                                          >
                                            {fiberDisplayName}
                                            {isUsed && ` - ${usedBy}`}
                                          </option>
                                        );
                                      }))}
                                    </select>
                                    </div>

                                    <div className="tap-pole tap-pole-zvar">
                                      <label>Zvar</label>
                                      <label className="zvar-prepinac">
                                        <input
                                          type="checkbox"
                                          checked={!!fiber.tap_zapisat_zvar}
                                          onChange={(e) =>
                                            handleUpdateFiberTap(
                                              ptIndex, fiberIndex,
                                              'tap_zapisat_zvar', e.target.checked)
                                          }
                                        />
                                        <span>Zapísať zvar</span>
                                      </label>
                                    </div>

                                    <div className="tap-pole tap-pole-loss">
                                    <label>Útlm (dB)</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={fiber.tap_loss_db || 0.1}
                                      onChange={(e) =>
                                        handleUpdateFiberTap(
                                          ptIndex,
                                          fiberIndex,
                                          'tap_loss_db',
                                          parseFloat(e.target.value)
                                        )
                                      }
                                      placeholder="0.1"
                                    />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>Zatiaľ žiadne priechodné káble</p>
                      <p className="empty-state-hint">
                        Kábel prechádza cez stĺp bez prerušenia, ale môžeš odtapovať jednotlivé
                        vlákna
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'splices' && (
                <div className="cassette-tab-content">
                  {/* Prípojky k zákazníkom. Sem patrí koniec vlákna, ktoré ide
                      k domu — aj vtedy, keď splitter, z ktorého vlákno prišlo,
                      je na inom stĺpiku. Technik tu potom v checkliste vidí
                      voľný port, na ktorý sa dá pripojiť zákazník. */}
                  <div className="cassette-section-header">
                    <h3>Prípojky k zákazníkom</h3>
                    <button className="btn-add" onClick={handleAddDrop}>
                      + Pridať prípojku
                    </button>
                  </div>

                  {(cassette.drops || []).length > 0 ? (
                    <div className="splice-list" style={{ marginBottom: '20px' }}>
                      {(cassette.drops || []).map((drop, index) => {
                        const port = portNaVlakne(portyNaVlaknach, drop.cable_id, drop.fiber);
                        const f = farbaVlakna(drop.fiber);
                        return (
                          <div key={index} className="splice-item">
                            <div className="splice-header">
                              <span>
                                Prípojka #{index + 1}
                                {f && (
                                  <span style={{
                                    marginLeft: '8px', padding: '1px 7px', borderRadius: '5px',
                                    fontSize: '11px', fontWeight: 700,
                                    background: f.hex, color: f.text,
                                    border: '1px solid rgba(0,0,0,0.2)',
                                  }}>{f.nazov}</span>
                                )}
                                {port ? (
                                  port.zakaznik ? (
                                    <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 700, color: '#b45309' }}>
                                      👤 {port.zakaznik}
                                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '4px' }}>
                                        (port {port.vystup} splittra na {port.poleName})
                                      </span>
                                    </span>
                                  ) : (
                                    <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 700, color: '#166534' }}>
                                      VOĽNÝ
                                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '4px' }}>
                                        — port {port.vystup} splittra {port.splitterTyp} na {port.poleName}
                                      </span>
                                    </span>
                                  )
                                ) : (
                                  <span style={{ marginLeft: '8px', fontSize: '12px', color: '#dc2626' }}>
                                    na tomto vlákne nie je žiadny zákaznícky splitter
                                  </span>
                                )}
                              </span>
                              <button className="btn-remove" onClick={() => handleRemoveDrop(index)}>✕</button>
                            </div>

                            <div className="splice-config">
                              <div className="splice-side">
                                <label>Kábel</label>
                                <select
                                  value={drop.cable_id}
                                  onChange={(e) => handleUpdateDrop(index, 'cable_id', parseInt(e.target.value))}
                                >
                                  {connectedCablesVystup.map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {getCableName(c.id)}{oznacOlt(c.id)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="splice-side">
                                <label>Vlákno</label>
                                <select
                                  value={drop.fiber}
                                  onChange={(e) => handleUpdateDrop(index, 'fiber', parseInt(e.target.value))}
                                >
                                  {poBufroch(drop.cable_id, Array.from(
                                    { length: fiberCountFromCableType(
                                        cables.find((c) => c.id === drop.cable_id)?.cable_type) },
                                    (_, i) => i + 1,
                                  ).map((n) => (
                                    <option key={n} value={n}>
                                      {getFiberDisplayNameForCable(drop.cable_id, n)}
                                    </option>
                                  )))}
                                </select>
                              </div>
                              <div className="splice-side">
                                <label>Popis (nepovinné)</label>
                                <input
                                  type="text"
                                  value={drop.label || ''}
                                  placeholder="napr. k domu č. 119"
                                  onChange={(e) => handleUpdateDrop(index, 'label', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '20px' }}>
                      Žiadne prípojky. Pridaj ju tam, kde sa vlákno zvára ku káblu
                      k zákazníkovi domov — aj keď splitter je na inom stĺpiku.
                    </p>
                  )}

                  {pripojkyInde.length > 0 && (
                    <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '-12px', marginBottom: '20px' }}>
                      ℹ️ Ďalšie prípojky ({pripojkyInde.length}) sú zapísané v{' '}
                      {druhaKazeta?.popis} tohto stĺpika
                      {pripojkyInde.map((d, i) => {
                        const fb = farbaVlakna(d.fiber);
                        return (
                          <span key={i}>
                            {i === 0 ? ': ' : ', '}
                            {getCableName(d.cable_id)} vl. {d.fiber}
                            {fb ? ` (${fb.nazov})` : ''}
                            {d.label ? ` — ${d.label}` : ''}
                          </span>
                        );
                      })}
                      . V checkliste stĺpika sa ukážu všetky.
                    </p>
                  )}

                  <div className="cassette-section-header">
                    <h3>Zvary</h3>
                    <button className="btn-add" onClick={handleAddSplice}>
                      + Pridať zvar
                    </button>
                  </div>

                  {cassette.splices && cassette.splices.length > 0 ? (
                    <div className="splice-list">
                      {cassette.splices.map((splice, index) => (
                        <div key={index} className="splice-item">
                          <div className="splice-header">
                            <span>
                              Zvar #{index + 1}
                              {(splice as any).z_odbocky && (
                                <span className="stitok-z-odbocky" title="Vznikol odbočením vlákna z priechodu. Cieľ sa mení v záložke Priechod.">
                                  z priechodu
                                </span>
                              )}
                              {/* Vlákno môže patriť zákazníkovi, ktorý visí na
                                  splittri na inom stĺpiku — tu by inak bol len
                                  zvar bez mena. */}
                              {zakaznikStitok(splice.to_cable_id, splice.to_fiber)
                                || zakaznikStitok(splice.from_cable_id, splice.from_fiber)}
                            </span>
                            <button
                              className="btn-remove"
                              onClick={() => handleRemoveSplice(index)}
                            >
                              ✕
                            </button>
                          </div>

                          <div className="splice-config">
                            <div className="splice-side">
                              <label>Zo zdroja</label>
                              <select
                                value={splice.from_source_type || 'cable'}
                                onChange={(e) => {
                                  const sourceType = e.target.value as 'cable' | 'olt_port';
                                  handleUpdateSplice(index, 'from_source_type', sourceType);
                                  if (sourceType === 'olt_port') {
                                    handleUpdateSplice(index, 'from_olt_port', 1);
                                    handleUpdateSplice(index, 'from_cable_id', undefined);
                                    handleUpdateSplice(index, 'from_fiber', undefined);
                                  } else {
                                    handleUpdateSplice(index, 'from_cable_id', connectedCables[0]?.id);
                                    handleUpdateSplice(index, 'from_fiber', 1);
                                    handleUpdateSplice(index, 'from_olt_port', undefined);
                                  }
                                }}
                              >
                                <option value="cable">Kábel</option>
                                {pole.pole_type === 'olt' && <option value="olt_port">OLT Port</option>}
                              </select>

                              {splice.from_source_type === 'olt_port' ? (
                                <>
                                  <label>OLT Port</label>
                                  <select
                                    value={splice.from_olt_port || 1}
                                    onChange={(e) =>
                                      handleUpdateSplice(index, 'from_olt_port', parseInt(e.target.value))
                                    }
                                  >
                                    {[...Array(pole.olt_port_count || 1)].map((_, i) => (
                                      <option key={i + 1} value={i + 1}>
                                        Port {i + 1}
                                      </option>
                                    ))}
                                  </select>
                                </>
                              ) : (
                                <>
                                  <label>Kábel</label>
                                  <select
                                    value={splice.from_cable_id}
                                    onChange={(e) =>
                                      handleUpdateSplice(index, 'from_cable_id', parseInt(e.target.value))
                                    }
                                  >
                                    {connectedCables.map((cable) => (
                                      <option key={cable.id} value={cable.id}>
                                        {getCableName(cable.id)}{oznacOlt(cable.id)}
                                      </option>
                                    ))}
                                  </select>

                                  <label>Vlákno</label>
                                  <select
                                    value={splice.from_fiber}
                                    onChange={(e) =>
                                      handleUpdateSplice(index, 'from_fiber', parseInt(e.target.value))
                                    }
                                  >
                                    {poBufroch(splice.from_cable_id, [...Array(getCableFiberCount(splice.from_cable_id || 0))].map((_, i) => {
                                      const fiberNum = i + 1;

                                      // Check if used in current cassette
                                      const cassetteUsage = splice.from_cable_id ?
                                        isFiberUsedInCurrentCassette(splice.from_cable_id, fiberNum, undefined, index) :
                                        { isUsed: false, usedBy: '' };

                                      // Vlákno, ktoré tadiaľto len prechádza, sa dá
                                      // použiť — len ho treba odbočiť. Preto sa
                                      // neblokuje, iba označí; odbočku ponúkne
                                      // tlačidlo pod zvarom.
                                      const lenPriechod = !!priechodBezOdbocky(
                                        splice.from_cable_id, fiberNum);
                                      const isDisabled = cassetteUsage.isUsed && !lenPriechod;
                                      const disabledReason = cassetteUsage.usedBy;
                                      const fiberDisplayName = splice.from_cable_id ?
                                        getFiberDisplayNameForCable(splice.from_cable_id, fiberNum) :
                                        `Fiber #${fiberNum}`;

                                      return (
                                        <option
                                          key={fiberNum}
                                          value={fiberNum}
                                          disabled={isDisabled}
                                        >
                                          {fiberDisplayName}
                                          {lenPriechod && ' — ide cez priechod'}
                                          {isDisabled && ` - ${disabledReason}`}
                                        </option>
                                      );
                                    }))}
                                  </select>
                                  {/* Show signal indicator for from fiber */}
                                  {splice.from_cable_id && splice.from_fiber && (
                                    <FiberSignalIndicator
                                      cableId={splice.from_cable_id}
                                      fiberNumber={splice.from_fiber}
                                      compact={true}
                                    />
                                  )}
                                </>
                              )}
                            </div>

                            <div className="splice-arrow">→</div>

                            <div className="splice-side">
                              <label>Do kábla</label>
                              <select
                                value={splice.to_cable_id}
                                onChange={(e) =>
                                  handleUpdateSplice(index, 'to_cable_id', parseInt(e.target.value))
                                }
                              >
                                {connectedCablesVystup.map((cable) => (
                                  <option key={cable.id} value={cable.id}>
                                    {getCableName(cable.id)}{oznacOlt(cable.id)}
                                  </option>
                                ))}
                              </select>

                              <label>Vlákno</label>
                              <select
                                value={splice.to_fiber}
                                onChange={(e) =>
                                  handleUpdateSplice(index, 'to_fiber', parseInt(e.target.value))
                                }
                              >
                                {poBufroch(splice.to_cable_id, [...Array(getCableFiberCount(splice.to_cable_id || 0))].map((_, i) => {
                                  const fiberNum = i + 1;

                                  // Check if used in current cassette
                                  const cassetteUsage = isFiberUsedInCurrentCassette(splice.to_cable_id, fiberNum, undefined, index);

                                  // If same cable, can't use same fiber as FROM
                                  const isSameCableAndFiber =
                                    splice.from_source_type === 'cable' &&
                                    splice.from_cable_id === splice.to_cable_id &&
                                    splice.from_fiber === fiberNum;

                                  const isDisabled = cassetteUsage.isUsed || isSameCableAndFiber;
                                  const disabledReason = isSameCableAndFiber ? 'Same as FROM' : cassetteUsage.usedBy;
                                  const fiberDisplayName = getFiberDisplayNameForCable(splice.to_cable_id || 0, fiberNum);

                                  return (
                                    <option
                                      key={fiberNum}
                                      value={fiberNum}
                                      disabled={isDisabled}
                                    >
                                      {fiberDisplayName}
                                      {isDisabled && ` - ${disabledReason}`}
                                    </option>
                                  );
                                }))}
                              </select>
                              {/* Show signal indicator for to fiber */}
                              {splice.to_cable_id && splice.to_fiber && (
                                <FiberSignalIndicator
                                  cableId={splice.to_cable_id}
                                  fiberNumber={splice.to_fiber}
                                  compact={true}
                                />
                              )}

                              {/* Reverse direction checkbox - only show if cable comes TO this pole */}
                              {(() => {
                                if (!splice.to_cable_id) return null;

                                const toCable = cables.find(c => c.id === splice.to_cable_id);
                                if (!toCable) return null;

                                const canBeReversed = toCable.to_pole_id === pole.id;

                                if (!canBeReversed) return null;

                                return (
                                  <div className="mt-2">
                                    <label className="flex items-center text-sm">
                                      <input
                                        type="checkbox"
                                        checked={splice.reverse_direction || false}
                                        onChange={(e) => {
                                          handleUpdateSplice(
                                            index,
                                            'reverse_direction',
                                            e.target.checked
                                          );
                                        }}
                                        className="mr-2"
                                      />
                                      Reverzné vlákno
                                    </label>
                                  </div>
                                );
                              })()}
                            </div>

                            {priechodBezOdbocky(splice.from_cable_id, splice.from_fiber) && (
                            <div className="ponuka-odbocky">
                              <div>
                                <b>Toto vlákno tadiaľto len prechádza.</b> Aby sa dalo
                                zvariť inam, treba ho vybrať z priechodu — spravím to
                                za teba. <b>Zvar ostane zapísaný</b>, aby bol
                                v dokumentácii aj v montážnom checkliste.
                              </div>
                              {splice.to_cable_id && splice.to_fiber ? (
                                <button
                                  className="btn-success"
                                  onClick={() => odbocPreZvar(index)}
                                >
                                  🔀 Vybrať z priechodu a nechať zvar
                                </button>
                              ) : (
                                <span className="ponuka-odbocky-cakam">
                                  Vyber ešte cieľový kábel a vlákno.
                                </span>
                              )}
                            </div>
                          )}

                          <div className="splice-loss">
                              <label>Útlm (dB)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={splice.loss_db || 0.1}
                                onChange={(e) =>
                                  handleUpdateSplice(index, 'loss_db', parseFloat(e.target.value))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>Zatiaľ žiadne zvary</p>
                      <p className="empty-state-hint">Zvar vytvoríte tlačidlom „Pridať zvar“</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'splitters' && (
                <div className="cassette-tab-content">
                  <div className="cassette-section-header">
                    <h3>Splittre</h3>
                    <button className="btn-add" onClick={handleAddSplitter}>
                      + Pridať splitter
                    </button>
                  </div>

                  {cassette.splitters && cassette.splitters.length > 0 ? (
                    <div className="splitter-list">
                      {cassette.splitters.map((splitter, index) => (
                        <div key={index} className="splitter-item">
                          <div className="splitter-header">
                            <span>Splitter č.{index + 1} ({splitter.type})</span>
                            <button
                              className="btn-remove"
                              onClick={() => handleRemoveSplitter(index)}
                            >
                              ✕
                            </button>
                          </div>

                          <div className="splitter-config">
                            <div className="splitter-input">
                              <div className="splitter-type">
                                <label>Typ splittera</label>
                                <select
                                  value={splitter.type}
                                  onChange={(e) =>
                                    handleUpdateSplitter(index, 'type', e.target.value)
                                  }
                                >
                                  {!splitterCatalog.some((t) => t.name === splitter.type) && splitter.type && (
                                    <option value={splitter.type}>{splitter.type}</option>
                                  )}
                                  {splitterCatalog.map((t) => (
                                    <option key={t.id ?? t.name} value={t.name}>
                                      {t.name} ({t.outputs_count} výstupov)
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="splitter-type">
                                <label>Vstupný zdroj</label>
                                <select
                                  value={splitter.input_source_type || 'cable'}
                                  onChange={(e) => {
                                    const sourceType = e.target.value as 'cable' | 'olt_port' | 'splitter_output';
                                    console.log('[Cassette] Changing input source to:', sourceType);
                                    const newSplitters = [...(cassette.splitters || [])];

                                    if (sourceType === 'olt_port') {
                                      newSplitters[index] = {
                                        ...newSplitters[index],
                                        input_source_type: sourceType,
                                        input_olt_port: 1,
                                        input_cable_id: undefined,
                                        input_fiber: undefined,
                                        input_splitter_index: undefined,
                                        input_splitter_output: undefined,
                                      };
                                    } else if (sourceType === 'splitter_output') {
                                      // Find first available splitter (not itself)
                                      const firstAvailableSplitterIndex = cassette.splitters?.findIndex((s, i) => i !== index) ?? 0;
                                      console.log('[Cassette] Setting splitter input to splitter index:', firstAvailableSplitterIndex);
                                      newSplitters[index] = {
                                        ...newSplitters[index],
                                        input_source_type: sourceType,
                                        input_splitter_index: firstAvailableSplitterIndex >= 0 ? firstAvailableSplitterIndex : 0,
                                        input_splitter_output: 0,
                                        input_cable_id: undefined,
                                        input_fiber: undefined,
                                        input_olt_port: undefined,
                                      };
                                      console.log('[Cassette] Updated splitter:', newSplitters[index]);
                                    } else {
                                      newSplitters[index] = {
                                        ...newSplitters[index],
                                        input_source_type: sourceType,
                                        input_cable_id: connectedCables[0]?.id,
                                        input_fiber: 1,
                                        input_olt_port: undefined,
                                        input_splitter_index: undefined,
                                        input_splitter_output: undefined,
                                      };
                                    }

                                    setCassette({
                                      ...cassette,
                                      splitters: newSplitters,
                                    });
                                  }}
                                >
                                  <option value="cable">Kábel</option>
                                  {pole.pole_type === 'olt' && <option value="olt_port">OLT Port</option>}
                                  {cassette.splitters && cassette.splitters.length > 1 && (
                                    <option value="splitter_output">Výstup splittera</option>
                                  )}
                                </select>
                              </div>

                              {splitter.input_source_type === 'olt_port' ? (
                                <>
                                  <div className="splitter-type">
                                    <label>OLT Port</label>
                                    <select
                                      value={splitter.input_olt_port || 1}
                                      onChange={(e) =>
                                        handleUpdateSplitter(index, 'input_olt_port', parseInt(e.target.value))
                                      }
                                    >
                                      {[...Array(pole.olt_port_count || 1)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                          Port {i + 1}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {(() => {
                                    const portRatio = calculateCascadeRatio(
                                      splitter.input_olt_port || 1,
                                      cassette,
                                      cables,
                                      poles
                                    );
                                    const isOverLimit = portRatio > 64;
                                    return (
                                      <div className="splitter-type" style={{
                                        background: isOverLimit ? '#fee' : '#efe',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        border: `1px solid ${isOverLimit ? '#fcc' : '#cfc'}`
                                      }}>
                                        <small>
                                          {isOverLimit ? '⚠️' : '✓'} Total Ratio: 1:{portRatio}
                                          {isOverLimit && ' (Exceeds 1:64 limit!)'}
                                        </small>
                                      </div>
                                    );
                                  })()}
                                </>
                              ) : splitter.input_source_type === 'splitter_output' ? (
                                <>
                                  <div className="splitter-type">
                                    <label>Zdrojový splitter</label>
                                    <select
                                      value={splitter.input_splitter_index ?? ''}
                                      onChange={(e) =>
                                        handleUpdateSplitter(index, 'input_splitter_index', parseInt(e.target.value))
                                      }
                                    >
                                      {cassette.splitters?.map((s, sIndex) => {
                                        // Don't allow selecting itself
                                        if (sIndex === index) return null;
                                        return (
                                          <option key={sIndex} value={sIndex}>
                                            Splitter č.{sIndex + 1} ({s.type})
                                          </option>
                                        );
                                      }).filter(Boolean)}
                                    </select>
                                  </div>

                                  <div className="splitter-type">
                                    <label>Výstupný port</label>
                                    <select
                                      value={splitter.input_splitter_output ?? ''}
                                      onChange={(e) =>
                                        handleUpdateSplitter(index, 'input_splitter_output', parseInt(e.target.value))
                                      }
                                    >
                                      {splitter.input_splitter_index !== undefined &&
                                       cassette.splitters?.[splitter.input_splitter_index]?.outputs?.map((output, outIndex) => (
                                        <option key={outIndex} value={outIndex}>
                                          Výstup č.{outIndex + 1}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </>
                              ) : splitter.input_source_type === 'cable' ? (
                                <>
                                  <div className="splitter-type">
                                    <label>Vstupný kábel</label>
                                    <select
                                      value={splitter.input_cable_id}
                                      onChange={(e) =>
                                        handleUpdateSplitter(
                                          index,
                                          'input_cable_id',
                                          parseInt(e.target.value)
                                        )
                                      }
                                    >
                                      {connectedCables.map((cable) => (
                                        <option key={cable.id} value={cable.id}>
                                          {getCableName(cable.id)}{oznacOlt(cable.id)}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="splitter-type">
                                    <label>Vstupné vlákno</label>
                                    <select
                                      value={splitter.input_fiber}
                                      onChange={(e) =>
                                        handleUpdateSplitter(
                                          index,
                                          'input_fiber',
                                          parseInt(e.target.value)
                                        )
                                      }
                                    >
                                      {poBufroch(splitter.input_cable_id, [...Array(getCableFiberCount(splitter.input_cable_id || 0))].map((_, i) => {
                                        const fiberNum = i + 1;

                                        // Check if used in current cassette (by other splitters/splices)
                                        const cassetteUsage = splitter.input_cable_id ?
                                          isFiberUsedInCurrentCassette(splitter.input_cable_id, fiberNum, index) :
                                          { isUsed: false, usedBy: '' };

                                        const lenPriechod = !!priechodBezOdbocky(
                                          splitter.input_cable_id, fiberNum);
                                        const isDisabled = cassetteUsage.isUsed && !lenPriechod;
                                        const disabledReason = cassetteUsage.usedBy;
                                        const fiberDisplayName = splitter.input_cable_id ?
                                          getFiberDisplayNameForCable(splitter.input_cable_id, fiberNum) :
                                          `Fiber #${fiberNum}`;

                                        return (
                                          <option
                                            key={fiberNum}
                                            value={fiberNum}
                                            disabled={isDisabled}
                                          >
                                            {fiberDisplayName}
                                            {lenPriechod && ' — ide cez priechod'}
                                            {isDisabled && ` - ${disabledReason}`}
                                          </option>
                                        );
                                      }))}
                                    </select>
                                    {/* Show signal indicator for selected fiber */}
                                    <FiberSignalIndicator
                                      cableId={splitter.input_cable_id}
                                      fiberNumber={splitter.input_fiber}
                                      compact={true}
                                    />

                                    {priechodBezOdbocky(splitter.input_cable_id, splitter.input_fiber) && (
                                      <div className="ponuka-odbocky">
                                        <div>
                                          <b>Toto vlákno tadiaľto len prechádza.</b> Aby ho
                                          splitter mohol brať ako vstup, treba ho vybrať
                                          z priechodu. Zvarenie na splitter je v checkliste
                                          úlohou pri vstupe splittra.
                                        </div>
                                        <button
                                          className="btn-success"
                                          onClick={() => odbocPreSplitter(
                                            splitter.input_cable_id!, splitter.input_fiber!)}
                                        >
                                          🔀 Vybrať z priechodu
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : null}
                            </div>

                            {/* Splitter Color and Master Settings */}
                            <div className="splitter-settings">
                              <div className="splitter-type">
                                <label>Farba splittera</label>
                                <input
                                  type="color"
                                  value={splitter.color || '#95a5a6'}
                                  onChange={(e) =>
                                    handleUpdateSplitter(index, 'color', e.target.value)
                                  }
                                  style={{ width: '100%', height: '40px', cursor: 'pointer' }}
                                />
                              </div>

                              <div className="splitter-checkbox-group">
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={splitter.is_master || false}
                                    onChange={(e) =>
                                      handleUpdateSplitter(index, 'is_master', e.target.checked)
                                    }
                                  />
                                  <span>📌 Nadradený splitter (svorček)</span>
                                </label>

                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={splitter.is_customer_splitter || false}
                                    onChange={(e) =>
                                      handleUpdateSplitter(index, 'is_customer_splitter', e.target.checked)
                                    }
                                  />
                                  <span>👥 Zákaznícky splitter (pre ONU)</span>
                                </label>

                                {cassette.splitters && cassette.splitters.length > 1 && (
                                  <label className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={splitter.determines_pole_color || false}
                                      onChange={(e) =>
                                        handleUpdateSplitter(index, 'determines_pole_color', e.target.checked)
                                      }
                                    />
                                    <span>🎨 Určuje farbu stĺpika na mape</span>
                                  </label>
                                )}

                                {splitterCatalog.find((t) => t.name === splitter.type)?.is_symmetric === false && (
                                  <label className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={(splitter as any).color_by_input_fiber || false}
                                      onChange={(e) =>
                                        handleUpdateSplitter(index, 'color_by_input_fiber', e.target.checked)
                                      }
                                    />
                                    <span>🌈 Farbiť stĺpik podľa vstupného vlákna (asymetrický)</span>
                                  </label>
                                )}
                              </div>
                            </div>

                            <div className="splitter-outputs">
                              <h4>
                                Výstupy ({splitter.outputs.length})
                                {(() => {
                                  // Koľko portov už má zákazníka — aby bolo na prvý pohľad
                                  // vidno, koľko sa dá ešte pripojiť.
                                  const obsadenych = (splitter.outputs as any[]).filter(
                                    (o) => o?.customer_name).length;
                                  if (obsadenych === 0) return null;
                                  return (
                                    <span style={{
                                      marginLeft: '8px',
                                      fontSize: '12px',
                                      fontWeight: 700,
                                      color: obsadenych === splitter.outputs.length ? '#b45309' : '#166534',
                                    }}>
                                      👤 {obsadenych}/{splitter.outputs.length} obsadených
                                    </span>
                                  );
                                })()}
                              </h4>
                              <div className="splitter-output-list">
                                {splitter.outputs.map((output, outputIndex) => {
                                  // Check if this port has an ONU connected
                                  const connectedONU = getONUForPort(index, outputIndex + 1);
                                  // Zákazník zapísaný technikom do kazety. Má prednosť pred ONU —
                                  // tabuľka onus je v praxi prázdna, kým cez inštalačný checklist
                                  // pribudlo 137 ľudí práve sem.
                                  const zakaznik = (output as any)?.customer_name as string | undefined;
                                  const farbaVystupu = farbaVlakna(outputIndex + 1);

                                  return (
                                  <div key={outputIndex} className="splitter-output-item">
                                    <span className="splitter-output-label">
                                      Výstup č.{outputIndex + 1}
                                      {farbaVystupu && (
                                        <span style={{
                                          marginLeft: '6px',
                                          padding: '1px 7px',
                                          borderRadius: '5px',
                                          fontSize: '11px',
                                          fontWeight: 700,
                                          background: farbaVystupu.hex,
                                          color: farbaVystupu.text,
                                          border: '1px solid rgba(0,0,0,0.2)',
                                        }}>
                                          {farbaVystupu.nazov}
                                        </span>
                                      )}
                                      {zakaznik ? (
                                        <span style={{
                                          marginLeft: '8px',
                                          fontSize: '12px',
                                          color: '#b45309',
                                          fontWeight: 700,
                                        }}>
                                          👤 {zakaznik}
                                          {(output as any).customer_address && (
                                            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '6px' }}>
                                              {(output as any).customer_address}
                                            </span>
                                          )}
                                          {output.cable_id && output.fiber && (() => {
                                            // Kam vlákno vedie — zákazník môže byť fyzicky
                                            // pripojený až na inom stĺpiku
                                            const konce = cables.find((c) => c.id === output.cable_id);
                                            if (!konce) return null;
                                            const druhy = poles.find((x) => x.id ===
                                              (konce.from_pole_id === pole.id || konce.from_pole_id === pole.parent_pole_id
                                                ? konce.to_pole_id : konce.from_pole_id));
                                            return (
                                              <span style={{ fontWeight: 400, color: '#2563eb', marginLeft: '6px' }}>
                                                → {getCableName(output.cable_id)} vl {output.fiber}
                                                {druhy ? ` (${druhy.name})` : ''}
                                              </span>
                                            );
                                          })()}
                                        </span>
                                      ) : !connectedONU && (
                                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#9ca3af' }}>
                                          voľný
                                        </span>
                                      )}
                                      {connectedONU && (
                                        <span style={{
                                          marginLeft: '8px',
                                          fontSize: '12px',
                                          color: '#10b981',
                                          fontWeight: 600
                                        }}>
                                          📡 {connectedONU.customer_name || connectedONU.name}
                                          {connectedONU.fiber_color && (
                                            <span style={{
                                              marginLeft: '6px',
                                              padding: '2px 8px',
                                              backgroundColor: '#f3f4f6',
                                              borderRadius: '4px',
                                              fontSize: '11px',
                                              fontWeight: 500
                                            }}>
                                              {connectedONU.fiber_color}
                                            </span>
                                          )}
                                        </span>
                                      )}
                                    </span>
                                    <select
                                      value={output.cable_id || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        handleUpdateSplitterOutput(
                                          index,
                                          outputIndex,
                                          'cable_id',
                                          value ? parseInt(value) : undefined
                                        );
                                        // If setting to empty, also clear fiber
                                        if (!value) {
                                          handleUpdateSplitterOutput(
                                            index,
                                            outputIndex,
                                            'fiber',
                                            undefined
                                          );
                                        }
                                      }}
                                    >
                                      <option value="">Nepripojené</option>
                                      {connectedCablesVystup.map((cable) => (
                                        <option key={cable.id} value={cable.id}>
                                          {getCableName(cable.id)}{oznacOlt(cable.id)}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      value={output.fiber || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        handleUpdateSplitterOutput(
                                          index,
                                          outputIndex,
                                          'fiber',
                                          value ? parseInt(value) : undefined
                                        );
                                      }}
                                      disabled={!output.cable_id}
                                    >
                                      {!output.cable_id && <option value="">Nevybrané</option>}
                                      {output.cable_id && [
                                        <option key="empty" value="">Nevybrané</option>,
                                        ...poBufroch(output.cable_id, [...Array(getCableFiberCount(output.cable_id))].map((_, i) => {
                                          const fiberNum = i + 1;
                                          const cableId = output.cable_id!; // Safe because we're inside output.cable_id && [...]

                                          // Check if used in current cassette (including other outputs of this splitter)
                                          const cassetteUsage = isFiberUsedInCurrentCassette(
                                            cableId,
                                            fiberNum,
                                            index, // current splitter index
                                            undefined, // no splice to exclude
                                            outputIndex // current output index to exclude
                                          );

                                          // If same cable as input, can't use same fiber
                                          const isSameCableAndFiber =
                                            splitter.input_source_type === 'cable' &&
                                            splitter.input_cable_id === cableId &&
                                            splitter.input_fiber === fiberNum;

                                          const isDisabled = cassetteUsage.isUsed || isSameCableAndFiber;
                                          const disabledReason = isSameCableAndFiber ? 'Same as INPUT' : cassetteUsage.usedBy;
                                          const fiberDisplayName = getFiberDisplayNameForCable(cableId, fiberNum);

                                          return (
                                            <option
                                              key={fiberNum}
                                              value={fiberNum}
                                              disabled={isDisabled}
                                            >
                                              {fiberDisplayName}
                                              {isDisabled && ` - ${disabledReason}`}
                                            </option>
                                          );
                                        }))
                                      ]}
                                    </select>
                                    {/* Reverse direction checkbox - only show when it makes sense */}
                                    {(() => {
                                      if (!output.cable_id) return null;

                                      const outputCable = cables.find(c => c.id === output.cable_id);
                                      if (!outputCable) return null;

                                      // Show checkbox only if the output cable comes TO current pole
                                      // (meaning we can send signal back in reverse direction)
                                      const canBeReversed = outputCable.to_pole_id === pole.id;

                                      if (!canBeReversed) return null;

                                      return (
                                        <div className="mt-2">
                                          <label className="flex items-center text-sm">
                                            <input
                                              type="checkbox"
                                              checked={output.reverse_direction || false}
                                              onChange={(e) => {
                                                handleUpdateSplitterOutput(
                                                  index,
                                                  outputIndex,
                                                  'reverse_direction',
                                                  e.target.checked
                                                );
                                              }}
                                              className="mr-2"
                                            />
                                            Reverzné vlákno
                                          </label>
                                        </div>
                                      );
                                    })()}

                                    {/* Show signal indicator for output fiber */}
                                    {output.cable_id && output.fiber && (
                                      <FiberSignalIndicator
                                        cableId={output.cable_id}
                                        fiberNumber={output.fiber}
                                        compact={true}
                                      />
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>Zatiaľ žiadne splittery</p>
                      <p className="empty-state-hint">Splitter vytvoríte tlačidlom „Pridať splitter“</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="dialog-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Zavrieť
            </button>
            <button
              type="button"
              className="btn-success"
              onClick={handleSave}
              disabled={loading || connectedCables.length === 0}
            >
              {loading ? 'Ukladá sa...' : 'Uložiť'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSaveAndClose}
              disabled={loading || connectedCables.length === 0}
            >
              {loading ? 'Ukladá sa...' : 'Uložiť a zavrieť'}
            </button>
          </div>
        </div>
      </div>

      {/* Undo Toast */}
      {undoToast && (
        <UndoToast
          message={undoToast.message}
          onUndo={undoToast.onUndo}
          onClose={() => setUndoToast(null)}
          duration={5}
        />
      )}
    </div>
  );
};

